import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { capitaliseFirstChar } from "utils/utils";
import MyProgress from "./MyProgress";
import MyKnowledgePoints from "./MyKnowledgePoints";
import MyBadges from "./MyBadges";
import MyLearningJourney from "./MyLearningJourney";
import Leaderboard from "../utils/Leaderboard";
import { MainContext } from "components/utils/Contexts";
import { getCollectionRequest } from "services/apiRequests";

interface StudentDashboardProps {
  ref?: any;
  headerRef: React.MutableRefObject<any>;
}

const StudentDashboard: React.FC<StudentDashboardProps> = forwardRef(
  ({ headerRef }, ref) => {
    // States definition
    const [leaderboardInfo, setLeaderboardInfo] = useState<{
      name: string;
      activate: boolean;
    }>({ name: "", activate: false });

    // Get context variables
    const { userClassId, userFirstName, userLastName } =
      useContext(MainContext);

    /**
     * Set the header image name
     */
    useEffect(() => {
      // Update the header name when dashboard is selected
      let currentHandler: any;
      if (headerRef.current) currentHandler = headerRef.current;

      // And set the title and subtitle
      currentHandler.setHeaderTitle("My Dashboard");
      currentHandler.setHeaderDescription(
        `Welcome, ${capitaliseFirstChar(userFirstName)}`,
      );

      getLeaderboardInfo();
    }, []);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      // Outer function to change the tab
      getFirstName() {
        return userFirstName;
      },
      getLastName() {
        return userLastName;
      },
    }));

    const getLeaderboardInfo = async () => {
      const response = await getCollectionRequest(
        `/api/classes/leaderboard-info?classId=${userClassId}`,
      );

      // Check if the response was successful
      if (!response.successful) return;

      // Get the content
      const content = response.content;

      // Check if the content has leaderboard information
      if (!content.leaderboard) return;

      // Get the leaderboard field
      const leaderboardInfo = content.leaderboard;

      // Set the state based on the information
      setLeaderboardInfo(leaderboardInfo || { name: "", activate: false });
    };

    return (
      <div className="w-full h-full justify-start items-start gap-5 inline-flex overflow-y-auto custom-scroll">
        {/* Main components */}
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-5 inline-flex">
          <div className="self-stretch justify-start items-end gap-[21px] inline-flex">
            <MyLearningJourney height={leaderboardInfo.activate ? 555 : 771} />
            <MyProgress
              height={leaderboardInfo.activate ? 555 : 771}
              userFirstName={userFirstName}
            />
          </div>
          {leaderboardInfo.activate && (
            <Leaderboard
              userClassId={userClassId as string}
              showStatus={false}
              leaderboardName={leaderboardInfo.name}
            />
          )}
        </div>
        {/* Sidebar */}
        <div className="w-[344px] h-full flex-col justify-start items-start gap-5 inline-flex">
          <div className="w-full h-full flex-grow flex flex-col gap-5">
            {/* Knowledge points */}
            <MyKnowledgePoints />
            {/* Badge points */}
            <MyBadges />
          </div>
        </div>
      </div>
    );
  },
);

export default StudentDashboard;
